import React, { useEffect, useRef } from 'react'
import { useWebSocket, OnEventCallback, WebSocketEvent } from './WebSocketContext'

/**
 * Hook to subscribe to WebSocket events
 * @param eventFilters - Event type(s) to filter on
 * @param onEventFunc - Callback function when matching event is received
 * @returns sendMessage function
 */
export const useWebSocketEvent = <TEvent extends WebSocketEvent = WebSocketEvent>(eventFilters: string | string[], onEventFunc: OnEventCallback<TEvent>): ((type: string, data: unknown) => void) => {
  const { subscribe, sendMessage } = useWebSocket()
  const onEventFuncRef = useRef(onEventFunc)
  const unsubscribeFuncRef = useRef(null)

  onEventFuncRef.current = onEventFunc

  useEffect(() => {
    return () => {
      unsubscribeFuncRef.current?.()
    }
  }, [])

  useEffect(() => {
    // Subscribe with stable callback that uses ref
    const unsubscribe = subscribe(eventFilters, (event) => {
      onEventFuncRef.current(event)
    })

    unsubscribeFuncRef.current = unsubscribe

    return () => {
      unsubscribe()
    }
  }, [subscribe, JSON.stringify(eventFilters)])

  if (process.env.NODE_ENV !== 'production') {
    React.useDebugValue(eventFilters)
  }

  return sendMessage
}

export default useWebSocketEvent
